import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { WizardCurrentResponse, WizardReadyResponse, WizardResponse, InventionWizardResponse } from '../models/wizard.model';
import { PatentDetails, ContractSession, ContractSessionResponse } from '../models/contractsession.model';
import { SimpleResponse } from '../models/genericResponse.model';
import { delayedRetryHttp } from '../shared/delayedRetry';
import { UtilityService } from './shared/utility.service';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Injectable()
export class WizardService {
    private baseUrl: string;
    private api: string = "api/Wizard";

    constructor(private httpClient: HttpClient, @Inject('BASE_URL') _baseUrl: string, private _utilityService: UtilityService) {
        this.baseUrl = _baseUrl + this.api;
    }

    private handleErrors(errorResponse: HttpErrorResponse) {
        if (errorResponse.error instanceof ErrorEvent) {
            console.error('Wizard Service Client Side Error: ', errorResponse.error.message);
        } else {
            throw errorResponse;
        }
    }

    async getWizardAsync(encryptedUser: string, wizardId: number, inventionId: number): Promise<WizardResponse | void> {
        const a = await this.httpClient
            .get<WizardResponse>(`${this.baseUrl}/GetWizard/${wizardId}/${inventionId}`, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'Authorization': 'Basic ' + encryptedUser
                })
            }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, null, "wizardId - " + wizardId + " inventionId - " + inventionId, encryptedUser, "WizardService", "getWizardAsync"); });

        return a;
    }

    async getCurrentWizardAsync(encryptedUser: string, wizardId: number, inventionId: number): Promise<WizardCurrentResponse | void> {
        const a = await this.httpClient
            .get<WizardCurrentResponse>(`${this.baseUrl}/GetCurrentWizard/${wizardId}/${inventionId}`, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'Authorization': 'Basic ' + encryptedUser
                })
            }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, null, "wizardId - " + wizardId + " inventionId - " + inventionId, encryptedUser, "WizardService", "getCurrentWizardAsync"); });

        return a;
    }

    async getInventionWizardAsync(encryptedUser: string, contactId: number): Promise<InventionWizardResponse | void> {
        const a = await this.httpClient
            .get<InventionWizardResponse>(`${this.baseUrl}/GetInventionWizard/${contactId}`, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'Authorization': 'Basic ' + encryptedUser
                })
            }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, null, "contactId - " + contactId, encryptedUser, "WizardService", "getInventionWizardAsync"); });

        return a;
    }

    async updateWizardAsync(encryptedUser: string, wizardId: number, inventionId: number, productId: number): Promise<WizardReadyResponse | void> {
        const a = await this.httpClient
            .get<WizardReadyResponse>(`${this.baseUrl}/UpdateWizard/${wizardId}/${inventionId}/${productId}`, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'Authorization': 'Basic ' + encryptedUser
                })
            }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, null, "wizardId - " + wizardId + " inventionId - " + inventionId + " productId - " + productId, encryptedUser, "WizardService", "updateWizardAsync"); });

        return a;
    }

    async getWizardNameAsync(encryptedUser: string, wizardId: number): Promise<WizardReadyResponse | void> {
        const a = await this.httpClient
            .get<WizardReadyResponse>(`${this.baseUrl}/GetWizardName/${wizardId}`, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'Authorization': 'Basic ' + encryptedUser
                })
            }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, null, "wizardId - " + wizardId, encryptedUser, "WizardService", "getWizardNameAsync"); });

        return a;
    }

    async createContractSessionAsync(encryptedUser: string, inventionId: number, contactId: number, productId: number, deliverPSOToEvolve: boolean, wizardId: number, isClientRemote: boolean, clpUserId: number, isSecondContract: boolean, countryID: number): Promise<ContractSessionResponse | void> {
        const a = await this.httpClient
            .get<ContractSessionResponse>(`${this.baseUrl}/CreateContractSession/${inventionId}/${contactId}/${productId}/${deliverPSOToEvolve}/${wizardId}/${isClientRemote}/${clpUserId}/${isSecondContract}/${countryID}`, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'Authorization': 'Basic ' + encryptedUser
                })
            }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, null, "inventionId - " + inventionId + " contactId - " + contactId + " productId - " + productId + " deliverPSOToEvolve - " + deliverPSOToEvolve + " wizardId - " + wizardId + " isClientRemote - " + isClientRemote, encryptedUser, "WizardService", "createContractSessionAsync"); });

        return a;
    }

    async updateContractSessionAsync(encryptedUser: string, contractSessionId: number, productId: number, deliverPSOToEvolve: boolean): Promise<ContractSessionResponse | void> {
        const a = await this.httpClient
            .get<ContractSessionResponse>(`${this.baseUrl}/UpdateContractSession/${contractSessionId}/${productId}/${deliverPSOToEvolve}`, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'Authorization': 'Basic ' + encryptedUser
                })
            }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, null, "contractSessionId - " + contractSessionId + " productId - " + productId + " deliverPSOToEvolve - " + deliverPSOToEvolve, encryptedUser, "WizardService", "updateContractSessionAsync"); });

        return a;
    }

    async getProductIdAsync(encryptedUser: string, inventionId: number, wizardId: number): Promise<ContractSessionResponse | void> {
        const a = await this.httpClient
            .get<ContractSessionResponse>(`${this.baseUrl}/GetProductId/${inventionId}/${wizardId}`, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'Authorization': 'Basic ' + encryptedUser
                })
            }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, null, "inventionId - " + inventionId + " wizardId - " + wizardId, encryptedUser, "WizardService", "getProductIdAsync"); });

        return a;
    }

    async getBillingSiteAsync(encryptedUser: string, inventionId: number, wizardId: number): Promise<SimpleResponse | void> {
        const a = await this.httpClient
            .get<SimpleResponse>(`${this.baseUrl}/GetBillingSite/${inventionId}/${wizardId}`, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'Authorization': 'Basic ' + encryptedUser
                })
            }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, null, "inventionId - " + inventionId + " wizardId - " + wizardId, encryptedUser, "WizardService", "getBillingSiteAsync"); });


        return a;
    }

    async getTextToNumberAsync(encryptedUser: string, key: string): Promise<SimpleResponse | void> {

        const a = await this.httpClient
            .get<SimpleResponse>(`${this.baseUrl}/GetTextToNumber/${key}`, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'Authorization': 'Basic ' + encryptedUser
                })
            }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, null, "key - " + key, encryptedUser, "WizardService", "getTextToNumberAsync"); });


        return a;
    }

    async getSiteAsync(encryptedUser: string): Promise<SimpleResponse | void> {
        const a = await this.httpClient
            .get<SimpleResponse>(`${this.baseUrl}/GetSite`, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'Authorization': 'Basic ' + encryptedUser
                })
            }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, null, "", encryptedUser, "WizardService", "getSiteAsync"); });


        return a;
    }

    async getEvolveCSSiteURLAsync(encryptedUser: string): Promise<SimpleResponse | void> {
        const a = await this.httpClient
            .get<SimpleResponse>(`${this.baseUrl}/GetEvolveCSSiteURL`, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'Authorization': 'Basic ' + encryptedUser
                })
            }).pipe(delayedRetryHttp()).toPromise().catch(err => { this._utilityService.handleErrors(err, null, "", encryptedUser, "WizardService", "getEvolveCSSiteURLAsync"); });


        return a;
    }

    getEvolveCSSiteURL(encryptedUser: string): Observable<SimpleResponse | void> {
        const http$ = this.httpClient
            .get<SimpleResponse>(`${this.baseUrl}/GetEvolveCSSiteURL`, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'Authorization': 'Basic ' + encryptedUser
                })
            }).pipe(
                delayedRetryHttp(),
                catchError(err => this._utilityService.handleErrors(err, null, "", encryptedUser, "WizardService", "getEvolveCSSiteURL"))
            );

        return http$;
    }
}
